/**
 * @file routes/pageLoader.ts
 * @description lazy loading of pages.
 *
 * @author ogarcia (ozkary)
 *
 */

import { lazy } from "react";

/**
 * Use this loader to load dynamic/lazy container. it has retry logic to handling the missing chunk errors
 * @param page func with container to load
 * @param reLoad number of retries
 * @returns a page container
 */
const pageLoader = <T>(page: () => Promise<T>, reLoad = 3): Promise<T> => {
  return new Promise((resolve, reject) => {
    page()
      .then(resolve)
      .catch((error: unknown) => {
        if (reLoad === 0) {
          reject(error);
          // this is probably a white page error. load the help page
          console.log(error);
          window.location.replace('/end');
          return;
        }

        pageLoader(page, --reLoad).then(resolve, reject);
      });
  });
};

/**
 * defines the route configuration
 */
export interface routeConfig {
  path: string[];
  component: string; 
  exact: boolean;
}

/**
 * route definition with laze loaded elements
 * v6+ support
 */
export interface routeInfo {
  key: string;
  path: string;
  elm: React.LazyExoticComponent<React.ComponentType<any>>; 
}

/**
 * expands a route configuration with a path array into single route entries for string path support
 * @param routes the route configuration
 * @returns a list of routes with one path per entry
 */
export const expandPagePaths = (routes: routeConfig[]) : routeInfo[] => {

  // v6+ expand the routes from arrays to single strings to support the new react-router version
  const pageRoutes = routes?.reduce((result: routeInfo[], routeItem) => {
    // import for the vsc or root path. - cannot to use variables for the path as of react.v16
    const cmp = routeItem.component.replace('/','');
    const elm = lazy(() => import(`../containers/${cmp}`));
    const routes = routeItem.path?.map((path) => {
      return {
        key: routeItem.component,
        path: path,
        elm: elm
      }  as routeInfo      
    });      
    result.push(...routes);   
    return result;   
  },[]);

  return pageRoutes;
}


/**
 * Merged route definitions to define the lazy element reference
 * @param routes the custom routes for a use case or flow vsc, ppm
 * @param baseRoutes the common routes for the app
 * @returns a merged version of the routes with the lazy element definition
 */
export const getPageRoutes = (routes: routeConfig[], baseRoutes: routeConfig[]) : routeInfo[] => {

  const mergedRoutes = [...routes, ...baseRoutes];

  // v6+ expand the routes from arrays to single strings to support the new react-router version
  const pageRoutes = mergedRoutes.reduce((result: routeInfo[], routeItem) => {    
    const cmp = routeItem.component.replace('/','');
    // import for the vsc or root path. - cannot to use variables for the path as of react.v16
    // removed the page loader , route chunks are not failing 
    const elm = routeItem.exact ? lazy(() => pageLoader(() => import(`../containers/${cmp}`))) : lazy(() => pageLoader(() => import(`../vsc/containers/${cmp}`)));
    const routes = routeItem.path?.map((path) => {
      return {
        key: cmp,
        path: path,
        elm: elm
      }  as routeInfo      
    });      
    result.push(...routes);
    return result;   
  },[]);

  return pageRoutes;

}

export default pageLoader;
